import { DndContext, DragOverlay, pointerWithin, useDraggable, useDroppable, type DragEndEvent } from "@dnd-kit/core";
import { useLiveQuery } from "dexie-react-hooks";
import { Filter } from "lucide-react";
import { useState } from "react";
import { toISODate, type Priority, type Task } from "@todoer/shared";
import { Select } from "../components/overlay.js";
import { TaskCard, useTaskSensors } from "../components/TaskList.js";
import { cx, PageHeader, useIsPhone } from "../components/ui.js";
import { db } from "../lib/db.js";
import { cmd, dispatch } from "../lib/sync.js";

const QUADRANTS: { priority: Priority; title: string; short: string; hint: string; color: string }[] = [
  { priority: 4, title: "Urgent & important", short: "Do", hint: "Do first · P1", color: "var(--color-p1)" },
  { priority: 3, title: "Important, not urgent", short: "Schedule", hint: "Schedule · P2", color: "var(--color-p2)" },
  { priority: 2, title: "Urgent, not important", short: "Delegate", hint: "Delegate · P3", color: "var(--color-p3)" },
  { priority: 1, title: "Neither", short: "Later", hint: "Later or drop · P4", color: "var(--color-text-faint)" },
];
type Q = (typeof QUADRANTS)[number];

function Item({ task }: { task: Task }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id });
  return <li><TaskCard task={task} showProject dnd={{ ref: setNodeRef, attributes, listeners, style: isDragging ? { opacity: 0.35 } : undefined }} /></li>;
}

function Quadrant({ q, tasks, dragging }: { q: Q; tasks: Task[]; dragging: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: `q:${q.priority}` });
  return (
    <section ref={setNodeRef} aria-labelledby={`q-${q.priority}`}
      className={cx("flex min-h-64 flex-col rounded-card bg-surface-sunken p-3 transition-[outline-color,background-color]", dragging && "outline-1 -outline-offset-1 outline-dashed outline-border-strong", isOver && "bg-brand-soft outline-brand")}>
      <header className="mb-3 flex items-center gap-2 px-1">
        <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: q.color }} />
        <h2 id={`q-${q.priority}`} className="min-w-0 truncate text-heading font-semibold">{q.title}</h2>
        <span className="tabular ml-auto text-meta text-text-faint">{tasks.length}</span>
      </header>
      <p className="-mt-2 mb-3 px-1 pl-[26px] text-meta text-text-faint">{q.hint}</p>
      {tasks.length
        ? <ul className="flex-1 space-y-2 overflow-y-auto">{tasks.map((t) => <Item key={t.id} task={t} />)}</ul>
        : <p className="grid flex-1 place-items-center rounded-control border border-dashed border-border-strong p-4 text-center text-meta text-text-faint">Drag tasks here</p>}
    </section>
  );
}

function SwitchChip({ q, count, active, onClick }: { q: Q; count: number; active: boolean; onClick: () => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: `q:${q.priority}:chip` });
  return (
    <button ref={setNodeRef} type="button" role="tab" aria-selected={active} onClick={onClick}
      className={cx("flex h-12 min-w-0 flex-1 flex-col items-center justify-center rounded-control text-meta font-medium transition-colors",
        active ? "bg-surface-raised text-text shadow-[0_1px_2px_rgb(0_0_0/0.08),0_0_0_1px_var(--color-border)]" : "text-text-muted", isOver && "ring-2 ring-brand")}>
      <span className="flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full" style={{ background: q.color }} />{q.short}</span>
      <span className="tabular text-caption text-text-faint">{count}</span>
    </button>
  );
}

/** Eisenhower matrix: priority decides the quadrant; drag between quadrants to re-prioritise. */
export function Matrix() {
  const [scope, setScope] = useState<"all" | "dated" | "week">("all");
  const [current, setCurrent] = useState<Priority>(4);
  const [dragId, setDragId] = useState<string | null>(null);
  const isPhone = useIsPhone();
  const week = toISODate(new Date(Date.now() + 7 * 86_400_000));
  const tasks = useLiveQuery(() => db.tasks.filter((t) => !t.completedAt && !t.parentId && (
    scope === "all" || (scope === "dated" && !!t.due) || (scope === "week" && !!t.due && t.due.date <= week)
  )).toArray(), [scope, week], []);
  const sensors = useTaskSensors();
  const onDragEnd = (e: DragEndEvent) => {
    setDragId(null);
    const over = e.over ? String(e.over.id) : null;
    if (!over?.startsWith("q:")) return;
    const p = Number(over.split(":")[1]) as Priority;
    const t = tasks.find((x) => x.id === String(e.active.id));
    if (t && t.priority !== p) void dispatch(cmd("item_update", { id: t.id, priority: p }));
  };
  const sorted = [...tasks].sort((a, b) => (a.due?.date ?? "9999").localeCompare(b.due?.date ?? "9999"));
  const of = (p: Priority) => sorted.filter((t) => t.priority === p);
  const dragTask = dragId ? tasks.find((t) => t.id === dragId) : undefined;

  return (
    <div>
      <PageHeader title="Matrix" subtitle="Drag tasks between quadrants to change their priority."
        actions={<div className="w-48 phone:w-40"><Select label="Which tasks" icon={Filter} value={scope} onChange={setScope} variant="chip" options={[
          { value: "all", label: "All open tasks" }, { value: "dated", label: "With a date" }, { value: "week", label: "Due within 7 days" },
        ]} /></div>} />
      <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={(e) => setDragId(String(e.active.id))} onDragCancel={() => setDragId(null)} onDragEnd={onDragEnd}>
        {isPhone ? (
          <>
            <div role="tablist" aria-label="Quadrant" className="mb-3 flex gap-0.5 rounded-[8px] bg-surface-sunken p-0.5">
              {QUADRANTS.map((q) => <SwitchChip key={q.priority} q={q} count={of(q.priority).length} active={current === q.priority} onClick={() => setCurrent(q.priority)} />)}
            </div>
            <Quadrant q={QUADRANTS.find((q) => q.priority === current)!} tasks={of(current)} dragging={!!dragId} />
          </>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {QUADRANTS.map((q) => <Quadrant key={q.priority} q={q} tasks={of(q.priority)} dragging={!!dragId} />)}
          </div>
        )}
        <DragOverlay>{dragTask && <div className="w-72 rotate-1"><TaskCard task={dragTask} dnd={{ dragging: true }} /></div>}</DragOverlay>
      </DndContext>
    </div>
  );
}
